import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { serviceKeys } from './query-key-factory'

import placeholderSvg from '@/assets/placeholder.svg'
import { QueryConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { ServiceOrigin, SortBy } from '@/types/service'
import { calculatePaginationRange, getSignedUrls } from '@/utils/db'

export type ListService = Awaited<ReturnType<typeof getServices>>['data'][0]
export type GetServicesOptions = {
  query?: string
  tid?: string
  origin?: ServiceOrigin
  sortBy?: SortBy
  page?: number
  itemsPerPage?: number
}

export const getServices = async ({
  query,
  tid,
  origin = 'all',
  sortBy = 'name',
  page = 1,
  itemsPerPage = 5,
}: GetServicesOptions) => {
  const supaQuery = supabase
    .from('services')
    .select(
      `*,
        technician:users(id, display_name, country, region_name, city),
        photos:service_photos(*),
        created_at`,
      { count: 'exact' },
    )
    .or('state.eq.active,state.eq.inactive')

  if (origin === 'predefined') {
    supaQuery.eq('is_predefined', true)
  } else if (origin === 'user') {
    if (!tid) {
      throw new Error('Technician ID (tid) is required when origin is "user".')
    }
    supaQuery.eq('technician_id', tid).eq('is_predefined', false)
  } else {
    if (tid) {
      supaQuery.or(`technician_id.eq.${tid},is_predefined.eq.true`)
    } else {
      supaQuery.eq('is_predefined', true)
    }
  }

  if (query) {
    supaQuery.ilike('name', `%${query}%`)
  }
  if (sortBy === 'price-asc') {
    supaQuery.order('price', { ascending: true })
  } else if (sortBy === 'price-desc') {
    supaQuery.order('price', { ascending: false })
  } else {
    supaQuery.order(sortBy, { ascending: false })
  }

  const { start, end } = calculatePaginationRange(page, itemsPerPage)
  supaQuery.range(start, end)

  // type Data = QueryData<typeof supaQuery>
  const { data, count, error } = await supaQuery

  if (error) {
    logger.error('Failed to get services:', {
      errorMessage: error,
      query,
      tid,
      sortBy,
      page,
    })
    throw error
  }

  if (!data || data.length === 0) {
    return {
      data: [],
      meta: {
        totalPages: page,
        page,
      },
    }
  }

  /**
   * fetch thumbnail signed url
   */
  const services = []
  for (let i = 0; i < data.length; i++) {
    const service = data[i]
    const mainPhoto = service.photos.find((p) => p.is_main)
    const thumbnail = mainPhoto?.storage_path
      ? (await getSignedUrls([mainPhoto.storage_path], 'service_photos'))[0]
      : placeholderSvg
    services.push({ ...service, thumbnail })
  }

  return {
    data: services,
    meta: {
      totalPages: Math.ceil((count || 0) / itemsPerPage),
      page,
    },
  }
}

// -- Normal query
type UseGetServicesOptions = GetServicesOptions & {
  page?: number
  queryConfig?: QueryConfig<typeof getServices>
}
export const useServices = ({
  query,
  tid,
  origin = 'all',
  sortBy = 'created_at',
  itemsPerPage = 10,
  page = 1,
  queryConfig,
}: UseGetServicesOptions) => {
  return useQuery({
    queryKey: serviceKeys.list({ tid, query, origin, sortBy, itemsPerPage, page }),
    queryFn: () => getServices({ query, tid, origin, sortBy, itemsPerPage, page }),
    ...queryConfig,
  })
}

const getInfiniteServicesQueryOptions = ({
  tid,
  query,
  origin = 'all',
  sortBy = 'created_at',
  itemsPerPage = 10,
}: GetServicesOptions) => {
  return {
    queryKey: serviceKeys['inf-list']({ tid, query, origin, sortBy, itemsPerPage }),
    queryFn: ({ pageParam }: { pageParam: number }) =>
      getServices({ query, tid, origin, sortBy, page: pageParam, itemsPerPage }),
    getNextPageParam: (lastPage: { meta: { page: number; totalPages: unknown } }) => {
      if (lastPage?.meta?.page === lastPage?.meta?.totalPages) return undefined
      return lastPage.meta.page + 1
    },
    initialPageParam: 1,
  }
}

// -- Infinite query
type UserGetInfiniteServicesOptions = GetServicesOptions & {
  queryConfig?: QueryConfig<typeof getInfiniteServicesQueryOptions>
}

export const useInfiniteServices = ({
  query,
  tid,
  origin = 'all',
  sortBy = 'name',
  itemsPerPage = 10,
  queryConfig,
}: UserGetInfiniteServicesOptions = {}) => {
  return useInfiniteQuery({
    ...getInfiniteServicesQueryOptions({ tid, query, origin, sortBy, itemsPerPage }),
    ...queryConfig,
  })
}
